import { UrlParam } from 'types/base';

export const Endpoint = {
  Orders: {
    List: 'v1/orders/list',
    CSV: 'v1/orders/csv',
    OrderById: (uuid: UrlParam) => `v1/orders/${uuid}`,
    export: 'v1/orders/csv',
  },
  Clients: {
    List: 'v1/clients/list',
    ById: (uuid: UrlParam) => `v1/clients/${uuid}`,
    Status: (uuid: UrlParam) => `v1/clients/${uuid}/status`,
    Balances: (uuid: UrlParam) => `v1/clients/${uuid}/balances`,
    RefreshKYCStatus: (uuid: UrlParam) => `v1/clients/${uuid}/refresh-kyc`,
    Cards: (uuid: UrlParam) => `v1/clients/${uuid}/cards`,
    IncreaseKYCAttempts: (uuid: UrlParam) => `v1/clients/${uuid}/increase-kyc-attempts`,
    GetQuote: (uuid: UrlParam) => `v1/clients/${uuid}/preview-refund`,
    Refund: (uuid: UrlParam) => `v1/clients/${uuid}/refund`,
    GetOrderPreview: (uuid: UrlParam) => `v1/clients/${uuid}/preview-order`,
    CreateOrder: (uuid: UrlParam) => `v1/clients/${uuid}/order`,
  },
  Users: {
    List: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/users`,
    Invite: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/users`,
    ResendInvite: (accountUuid: UrlParam, userUuid: UrlParam) => `v2/accounts/${accountUuid}/users/${userUuid}`,
    RevokeAccess: (accountUuid: UrlParam, userUuid: UrlParam) => `v2/accounts/${accountUuid}/users/${userUuid}`,
    ChangePassword: 'v2/users/change-password',
    AcceptInvite: (key: UrlParam) => `v1/users/invite/accept?inviteKey=${key}`,
    Create: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/users`,
    ById: (uuid: UrlParam) => `v1/users/${uuid}`,
  },
  ApiKeys: {
    List: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/api-keys`,
    Create: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/api-keys`,
    Update: (accountUuid: UrlParam, apiKeyUuid: UrlParam) => `v2/accounts/${accountUuid}/api-keys/${apiKeyUuid}`,
    Delete: (accountUuid: UrlParam, apiKeyUuid: UrlParam) => `v2/accounts/${accountUuid}/api-keys/${apiKeyUuid}`,
  },
  EventDestinations: {
    List: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/event-destinations/list`,
    Create: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/event-destinations`,
    ById: (accountUuid: UrlParam, destinationUuid: UrlParam) => `v2/accounts/${accountUuid}/event-destinations/${destinationUuid}`,
  },
  Accounts: {
    List: 'v1/accounts/list',
    Available: '/v2/accounts/available',
    DirectAccess: '/v2/accounts/direct-access',
    GroupCreate: 'v2/group-accounts',
    ById: (uuid: UrlParam) => `v2/accounts/${uuid}`,
    UserByAccountId: (uuid: UrlParam) => `v2/accounts/${uuid}/users`,
    AccountUserAccessLevel: (uuid: UrlParam, userUuid: UrlParam) => `v2/accounts/${uuid}/users/${userUuid}`,
    ApplePayDomains: (uuid: UrlParam) => `v1/accounts/${uuid}/apple-pay-domains`,
  },
  Merchants: {
    Create: 'v2/merchants',
    List: 'v2/merchants/list',
    ById: (uuid: UrlParam) => `v2/merchants/${uuid}`,
    OnboardingToken: (uuid: UrlParam) => `v2/merchants/${uuid}/onboarding-token`,
    BankAccountsById: (uuid: UrlParam) => `v2/merchants/${uuid}/bank-accounts`,
    BankAccountsByIdAutoCreate: (uuid: UrlParam) => `v2/merchants/${uuid}/bank-accounts/auto-create`,
    BankAccountById: (merchantUuid: UrlParam, bankAccountUuid: UrlParam) =>
      `v1/pwc/merchants/${merchantUuid}/bank-accounts/${bankAccountUuid}`,
    Kyb: (uuid: UrlParam) => `/v2/merchants/${uuid}/kyb`,
    BridgeKycInfo: (uuid: UrlParam) => `/v2/merchants/${uuid}/bridge-kyc-info`,
    AutoKyb: (uuid: UrlParam) => `/v2/merchants/${uuid}/auto-kyb`,
    TitanKybData: (shift4MerchantId: UrlParam, includeAllOwners?: boolean) =>
      `/v2/merchants/shift4/${shift4MerchantId}${includeAllOwners ? '?includeAllOwners=true' : ''}`,
    PayoutSettings: (uuid: UrlParam) => `v2/merchants/${uuid}/payout-settings`,
    PayoutDestinations: (uuid: UrlParam) => `v2/merchants/${uuid}/pwc-settlement-destinations`,
    DeletePayoutDestination: (uuid: UrlParam, payoutDestinationUuid: UrlParam, payoutDestinationType: UrlParam) =>
      `v2/merchants/${uuid}/pwc-settlement-destinations/${payoutDestinationUuid}/${payoutDestinationType}`,
    RefundReserve: (uuid: UrlParam) => `v2/merchants/${uuid}/refund-reserve`,
    // Use this endpoint only on dev and sandbox environments
    simulatePayout: (uuid: UrlParam) => `v1/pwc/merchants/${uuid}/payout`,
    AssociatedPersonsList: (uuid: UrlParam) => `/v2/merchants/${uuid}/associated-persons`,
    AssociatedPersonById: (merchantUuid: UrlParam, personUuid: UrlParam) =>
      `/v2/merchants/${merchantUuid}/associated-persons/${personUuid}`,
    CreateAssociatedPerson: (uuid: UrlParam) => `/v2/merchants/${uuid}/associated-persons`,
    UpdateAssociatedPerson: (merchantUuid: UrlParam, personUuid: UrlParam) =>
      `/v2/merchants/${merchantUuid}/associated-persons/${personUuid}`,
    DeleteAssociatedPerson: (merchantUuid: UrlParam, personUuid: UrlParam) =>
      `/v2/merchants/${merchantUuid}/associated-persons/${personUuid}`,
  },
  Refunds: {
    CancelByUuid: (orderUuid: UrlParam, refundUuids: UrlParam) => `v2/orders/${orderUuid}/refunds/${refundUuids}/cancel`,
    AccountMemoInstructions: (merchantUuid: UrlParam) => `v2/refunds/account/${merchantUuid}/memo-instructions`,
    AccountWalletTransactions: (merchantUuid: UrlParam) => `v2/refunds/account/${merchantUuid}/wallet/transactions`,
    AccountWalletTopup: (merchantUuid: UrlParam, topupUuid: UrlParam) => `v2/refunds/account/${merchantUuid}/wallet/topups/${topupUuid}`,
  },
  Payouts: {
    List: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/pwc-settlement/list`,
    ById: (accountUuid: UrlParam, settlementUuid: UrlParam) => `v2/accounts/${accountUuid}/pwc-settlement/${settlementUuid}`,
    Retry: {
      ById: (accountUuid: UrlParam, settlementUuid: UrlParam) => `v2/accounts/${accountUuid}/pwc-settlement/${settlementUuid}/retry`,
    },
  },
  CryptoPayouts: {
    Create: 'v2/crypto-payouts',
    ById: (uuid: UrlParam) => `v2/crypto-payouts/${uuid}`,
    Receipt: (uuid: UrlParam) => `v2/crypto-payouts/${uuid}/receipt`,
    CancelById: (uuid: UrlParam) => `v2/crypto-payouts/${uuid}/cancel`,
    List: 'v2/crypto-payouts/list',
    CSV: 'v2/crypto-payouts/csv',
  },
  PwcCustomers: {
    List: (accountUuid: UrlParam) => `v2/accounts/${accountUuid}/pwc-customers/list`,
    CustomerById: (accountUuid: UrlParam, customerUuid: UrlParam) => `v2/accounts/${accountUuid}/pwc-customers/${customerUuid}`,
  },
  PwcOrders: {
    Root: 'v2/orders',
    List: 'v2/orders/list',
    CSV: 'v2/orders/csv',
    OrderById: (uuid: UrlParam) => `v2/orders/${uuid}`,
    UnlockByUuid: (uuid: UrlParam) => `v2/orders/${uuid}/unlock`,
    CancelByUuid: (uuid: UrlParam) => `v2/orders/${uuid}/cancel`,
    RefundByUuid: (uuid: UrlParam) => `v2/orders/${uuid}/refunds`,
  },
  StaticAssets: {
    Upload: 'v2/images',
  },
  Assets: {
    List: (isActive: boolean) => `v1/assets${isActive ? '?isActive=true' : ''}`,
    CountriesList: 'v2/countries',
  },
  PwcAssets: {
    List: (merchantUuid?: string) => `v2/assets${merchantUuid ? `?merchantUuid=${merchantUuid}` : ''}`,
    FiatList: 'v2/assets/fiat',
  },
  Profile: {
    Me: 'v2/users/me',
  },
  Reconciliations: {
    List: 'v1/reconciliations/list',
    CSV: (uuid: UrlParam) => `v1/reconciliations/${uuid}/csv`,
    ById: (uuid: UrlParam) => `v1/reconciliations/${uuid}`,
  },
};

export default Endpoint;
